import * as React from "react";
import * as runtime from "react/jsx-runtime";
import { compile, run } from "@mdx-js/mdx";
import rehypeHighlight from "rehype-highlight";

// Client-side renderer for the raw markdown strings stored on
// ProjectDetail (whyBuilt/myRole/hardestProblem/statusDetails/
// lessonsLearned) — unlike blog posts, these are never compiled at
// publish-time (no `compiledJs` column on project_details), so this
// compiles + runs them in the browser with the same @mdx-js/mdx
// compile/run pair blog_.$slug.tsx's useCompiledMdx uses for pre-compiled
// output. rehype-highlight gives fenced code blocks real token classes
// (`hljs-*`, mapped to theme vars in app.css) instead of plain text.

type MDXContentComponent = (props: {
  components?: Record<string, React.ComponentType<any>>;
}) => React.ReactElement;

function useCompiledMarkdown(source: string | null | undefined) {
  const [Content, setContent] = React.useState<MDXContentComponent | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!source) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setError(null);
    compile(source, {
      outputFormat: "function-body",
      development: false,
      rehypePlugins: [rehypeHighlight],
    })
      .then((compiled) => run(String(compiled), { ...runtime, baseUrl: import.meta.url }))
      .then((mod) => {
        if (!cancelled) setContent(() => mod.default as MDXContentComponent);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return { Content, error };
}

function extractText(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (React.isValidElement(node)) {
    return extractText((node.props as { children?: React.ReactNode }).children);
  }
  return "";
}

function isDiffLanguage(node: React.ReactNode): boolean {
  if (!React.isValidElement(node)) return false;
  const className = (node.props as { className?: string }).className ?? "";
  return /language-diff/.test(className);
}

// Collapsible before/after diff, ported from design-reference's
// `.diff-toggle` / `.diff-wrap` pattern (blog-gc-post.html) — used here for
// any project markdown that fences a code block as ```diff, so a future
// tour-guide write-up can show a real bug fix the same way the GC post
// does, without needing custom MDX JSX syntax in stored content.
function DiffBlock({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const lines = extractText(children).replace(/\n$/, "").split("\n");

  return (
    <>
      <button type="button" className="diff-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? "▾ hide diff" : "▸ show diff"}
      </button>
      <div className={`diff-wrap${open ? " open" : ""}`}>
        <div className="code-block" style={{ marginTop: 0 }}>
          {lines.map((line, i) => {
            const cls = line.startsWith("+") ? "add" : line.startsWith("-") ? "rm" : "";
            return (
              <div key={i} className={cls ? `diff-line ${cls}` : "diff-line"}>
                {line}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function CopyButton({ getText }: { getText: () => string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      className="copy-btn"
      onClick={() => {
        navigator.clipboard?.writeText(getText()).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? "copied" : "copy"}
    </button>
  );
}

function Pre(props: React.ComponentPropsWithoutRef<"pre">) {
  const child = React.Children.only(props.children);
  if (isDiffLanguage(child)) {
    return <DiffBlock>{child}</DiffBlock>;
  }
  return (
    <div className="code-block">
      <CopyButton getText={() => extractText(child)} />
      <pre>{props.children}</pre>
    </div>
  );
}

export interface MarkdownProps {
  source: string | null | undefined;
  /** Applied to the top-level <ul>, e.g. "lesson-list" for a lessons-learned section. */
  listClassName?: string;
}

export function Markdown({ source, listClassName }: MarkdownProps) {
  const { Content, error } = useCompiledMarkdown(source);

  const components = React.useMemo(() => {
    const base: Record<string, React.ComponentType<any>> = { pre: Pre };
    if (listClassName) {
      base.ul = (p: React.ComponentPropsWithoutRef<"ul">) => (
        <ul className={listClassName} {...p} />
      );
    }
    return base;
  }, [listClassName]);

  if (!source) return null;
  if (error) {
    return <p className="section-sub">// failed to render content: {error}</p>;
  }
  if (!Content) return null;
  return <Content components={components} />;
}

export default Markdown;
